import { Suspense, lazy } from 'react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { GoogleReCaptchaProvider } from '@google-recaptcha/react'
import {
  Outlet,
  createBrowserRouter,
  RouterProvider,
  Navigate,
  redirect,
  redirectDocument,
} from 'react-router-dom'
import {
  RequireAuth,
  protectedLoader,
} from 'components/Common/Routes/PrivateRoute'
import { LocationWatcher } from 'utils/hooks/useGoBack'
import TableContextProvider from 'components/Common/Table/Context/Provider'
import GuestLoginDialogProvider from 'components/Common/Modals/Login/Context/Provider'
import ScreenProvider from 'utils/screen/Provider'
import NotificationProvider from 'utils/notification/Provider'
import { useUserBack } from 'utils/hooks/useUserBack'
import { useGTMInit } from 'utils/analytics/gtm'
import useSubscriptions from 'components/Common/Subscriptions'
import { PageLoaderWithMenu } from 'components/Common/Loader/PageLoader'
import {
  LOGIN_PAGE_PATH,
  PAYMENT_PAGE_PATH,
  SIGNUP_PAGE_PATH,
  ADMINISTRATION_RETAIL_PUBLIC_PAGES,
  ACCOUNT_SETUP_PATH,
  PUBLIC_CAPTURED_WINE_PATH,
} from 'utils/consts/path'
import Login from 'components/Pages/Login'
import Signup from 'components/Pages/Signup'
import ForgotPassword from 'components/Pages/ForgotPassword'
import ConfirmPassword from 'components/Pages/ConfirmPassword'
import VerifyCompany from 'components/Pages/VerifyCompany'
import Error500 from 'components/Common/Errors/500'
import Error404 from 'components/Common/Errors/404'
import DynamicImportErrorBoundary from 'components/Common/Errors/DynamicImportError'
import Payment from 'components/Pages/Payment'
import ConfirmMail from 'components/Pages/ConfirmMail'
import AuthenticatedLayout from 'components/Common/Layout/AuthenticatedLayout'
import { Roles, PersonCategories } from 'utils/consts/userRoles'
import UserRedirect from 'components/Pages/Homepage/UserRedirect'
import { isLogin } from 'utils/token'
import { getLocaleForPublicReview } from 'utils/getLocaleId'
import {
  productNameLoader,
  wineNameLoader,
} from 'components/Pages/Wine/View/_methods'
import { capturedWineNameLoader } from 'components/Pages/CapturedWine/_methods/capturedWineNameLoader'
import PublicCapturedWineView from 'components/Pages/PublicCapturedWine'
import { processUrlToken } from 'utils/token'

const AdministrationRedirect = lazy(
  () => import('components/Pages/Administration/Redirect')
)
const AdministrationUsers = lazy(
  () => import('components/Pages/Administration/Content/Users')
)
const AdministrationReviews = lazy(
  () => import('components/Pages/Administration/Content/Reviews')
)
const AdministrationCompanies = lazy(
  () => import('components/Pages/Administration/Content/Verification')
)
const AdministrationOffers = lazy(
  () => import('components/Pages/Administration/Content/Offers')
)
const AdministrationRequests = lazy(
  () => import('components/Pages/Administration/Content/Requests')
)
const AdministrationNewRequest = lazy(
  () => import('components/Pages/Administration/RequestCreate')
)
const AdministrationMatchedProducers = lazy(
  () =>
    import('components/Pages/Administration/Content/Requests/MatchedProducers')
)
const AdministrationPotentialProducers = lazy(
  () =>
    import(
      'components/Pages/Administration/Content/Requests/PotentialProducers'
    )
)
const AdministrationImages = lazy(
  () => import('components/Pages/Administration/Content/Images')
)
const AdministrationWineImages = lazy(
  () => import('components/Pages/Administration/Content/WineImages')
)
const AdministrationImageCreate = lazy(
  () => import('components/Pages/Image/Create')
)
const AdministrationELabels = lazy(
  () => import('components/Pages/Administration/Content/ELabels')
)
const AdministrationPublicPages = lazy(
  () => import('components/Pages/Administration/Content/RetailPublicPages')
)
const RetailPublicPageCreate = lazy(
  () => import('components/Pages/RetailPublicPage/Create')
)
const RetailPublicPageEdit = lazy(
  () => import('components/Pages/RetailPublicPage/Edit')
)
const AdministrationRetailOffers = lazy(
  () => import('components/Pages/Administration/Content/RetailOffers')
)
const AdministrationRetailOfferEdit = lazy(
  () => import('components/Pages/RetailOffer/Edit')
)

const AdministrationPriceSources = lazy(
  () => import('components/Pages/Administration/Content/PriceSources')
)
const AdministrationWineSites = lazy(
  () => import('components/Pages/Administration/Content/WineSites')
)
const AdministrationEShopCreate = lazy(
  () => import('components/Pages/Merchant/Create')
)
const AdministrationEShopEdit = lazy(
  () => import('components/Pages/Merchant/Edit')
)
const AdministrationMentions = lazy(
  () => import('components/Pages/Administration/Content/Mentions')
)
const AdministrationMentionsPrices = lazy(
  () => import('components/Pages/Administration/Content/MentionsPrices')
)
const AdministrationMentionsPricesVerifyRetailOffers = lazy(
  () =>
    import(
      'components/Pages/Administration/Content/MentionsPrices/VerifyRetailOffersPage'
    )
)
const AdministrationMentionFacts = lazy(
  () => import('components/Pages/Administration/Content/MentionFacts')
)
const AdministrationMentionFactsVerifyFacts = lazy(
  () =>
    import(
      'components/Pages/Administration/Content/MentionFacts/VerifyFactsPage'
    )
)
const AdministrationMentionEdit = lazy(
  () => import('components/Pages/Administration/Content/Mentions/Edit')
)
const AdministrationMentionsCluster = lazy(
  () => import('components/Pages/Administration/Content/Mentions/Cluster')
)
const AdministrationMentionsClusterPath = lazy(
  () => import('components/Pages/Administration/Content/Mentions/ClusterPath')
)
const AdministrationMentionCreate = lazy(
  () => import('components/Pages/Mention/Create')
)
const AdministrationMentionsClusterLinkMentions = lazy(
  () =>
    import(
      'components/Pages/Administration/Content/Mentions/Cluster/LinkMentions'
    )
)
const AdministrationMentionCompare = lazy(
  () => import('components/Pages/Administration/Content/Mentions/Compare')
)
const AdministrationClusters = lazy(
  () => import('components/Pages/Administration/Content/Clusters')
)
const AdministrationVerifiedRelations = lazy(
  () => import('components/Pages/Administration/Content/VerifiedRelations')
)
const TradeOfferPriceHistory = lazy(
  () => import('components/Pages/OfferPriceHistory')
)
const Offer = lazy(() => import('components/Pages/Offer'))
const Homepage = lazy(() => import('components/Pages/Homepage'))
const MyCompany = lazy(() => import('components/Pages/MyCompany'))
// const ViewCompany = lazy(() => import('components/Pages/Company/View'))
const EditCompany = lazy(() => import('components/Pages/Company/Edit'))
const MyWinesBrands = lazy(() => import('components/Pages/MyWines/Brands'))
const ProducerCreate = lazy(() => import('components/Pages/Producer/Create'))
const ProducerEdit = lazy(() => import('components/Pages/Producer/Edit'))
const ProducerView = lazy(() => import('components/Pages/Producer/View'))
const CreateBrand = lazy(() => import('components/Pages/Brands/Create'))
const ViewBrand = lazy(() => import('components/Pages/Brands/View'))
const EditBrand = lazy(() => import('components/Pages/Brands/Edit'))
const ExhibitionCreate = lazy(
  () => import('components/Pages/Exhibition/Create')
)
const ExhibitionEdit = lazy(() => import('components/Pages/Exhibition/Edit'))
const AwardCreate = lazy(() => import('components/Pages/Award/Create'))
const AwardEdit = lazy(() => import('components/Pages/Award/Edit'))
const GeoIndicationCreate = lazy(
  () => import('components/Pages/GeoIndication/Create')
)
const GeoIndicationEdit = lazy(
  () => import('components/Pages/GeoIndication/Edit')
)
const RewardCreate = lazy(() => import('components/Pages/Award/Reward/Create'))
const RewardEdit = lazy(() => import('components/Pages/Award/Reward/Edit'))
const ELabelEdit = lazy(() => import('components/Pages/ELabel/Edit'))
const ELabelCreate = lazy(() => import('components/Pages/ELabel/Create'))
const CreateCompany = lazy(() => import('components/Pages/Company/Create'))
const WineCreate = lazy(() => import('components/Pages/Wine/Create'))
const WineEdit = lazy(() => import('components/Pages/Wine/Edit'))
const WineRedirectToSlug = lazy(
  () => import('components/Pages/Wine/View/RedirectToSlug')
)
const WineRating = lazy(() => import('components/Pages/Wine/Rating'))
const WineView = lazy(() => import('components/Pages/Wine/View'))
const ProductSnapshotView = lazy(
  () => import('components/Pages/ProductSnapshot')
)
const ReviewView = lazy(() => import('components/Pages/Review/View'))
const SellWineRequests = lazy(
  () => import('components/Pages/SellWine/Requests')
)
const SellWineMyWarehouses = lazy(
  () => import('components/Pages/SellWine/MyWarehouses')
)
const SellWineAddNewOfferV2 = lazy(
  () => import('components/Pages/SellWine/AddNewOffer/Create')
)
const SellWineEditOffer = lazy(
  () => import('components/Pages/SellWine/AddNewOffer/Edit')
)
const BuyWineWines = lazy(() => import('components/Pages/BuyWine/Wines'))
const BuyWineAnalysis = lazy(() => import('components/Pages/BuyWine/Analysis'))
const BuyWineDraftRequestEdit = lazy(
  () => import('components/Pages/BuyWine/RequestEdit')
)
const BuyWineAppellationRating = lazy(
  () => import('components/Pages/BuyWine/Analysis/AppellationRating')
)
const BuyWineAddMyDeal = lazy(
  () => import('components/Pages/BuyWine/AddMyDeal')
)
const BuyWineRequestCreate = lazy(
  () => import('components/Pages/BuyWine/RequestCreate')
)
const CapturedWines = lazy(() => import('components/Pages/CapturedWines'))
const CapturedWinesBuyers = lazy(
  () => import('components/Pages/CapturedWinesBuyers')
)
const CapturedWineView = lazy(() => import('components/Pages/CapturedWine'))
const CapturedWineEdit = lazy(
  () => import('components/Pages/CapturedWine/Edit')
)

const CapturedWinesRouter = () => {
  const category = localStorage.getItem('wwl-person_category')
  if (category === PersonCategories.PRODUCER) {
    return <CapturedWines />
  }
  return <CapturedWinesBuyers />
}

const Layout = () => {
  return (
    <>
      <NotificationProvider>
        <ScreenProvider>
          <GuestLoginDialogProvider>
            <GoogleOAuthProvider clientId={process.env.GOOGLE_CLIENT_ID || ''}>
              <Suspense fallback={<PageLoaderWithMenu />}>
                <Outlet />
              </Suspense>
              <LocationWatcher />
            </GoogleOAuthProvider>
          </GuestLoginDialogProvider>
        </ScreenProvider>
      </NotificationProvider>
    </>
  )
}

const AuthPagesLayout = () => {
  return (
    <GoogleReCaptchaProvider
      type='v2-checkbox'
      siteKey={process.env.GOOGLE_RECAPTCHA_API_KEY || ''}
    >
      <Outlet />
    </GoogleReCaptchaProvider>
  )
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <DynamicImportErrorBoundary />,
    children: [
      {
        path: '/acc',
        element: <UserRedirect />,
      },
      {
        element: <AuthPagesLayout />,
        children: [
          {
            path: LOGIN_PAGE_PATH,
            element: <Login />,
          },
          {
            path: SIGNUP_PAGE_PATH,
            element: <Signup />,
          },
        ],
      },
      {
        path: '/acc/confirm-password',
        element: <ConfirmPassword />,
      },
      {
        path: '/acc/forgot-password',
        element: <ForgotPassword />,
      },
      {
        path: '/acc/verify-company',
        element: <VerifyCompany />,
      },
      {
        path: '/acc/confirm-mail',
        element: <ConfirmMail />,
      },
      {
        path: '/acc/change-email',
        element: <ConfirmMail />,
      },
      {
        path: PAYMENT_PAGE_PATH,
        element: <Payment />,
      },
      {
        path: '/acc/error',
        element: <Error500 />,
      },
      {
        path: '/acc/download-app',
        lazy: () => import('components/Pages/DownloadApp'),
      },
      {
        path: PUBLIC_CAPTURED_WINE_PATH,
        element: <PublicCapturedWineView />,
      },

      {
        path: '/acc',
        element: (
          <RequireAuth>
            <AuthenticatedLayout />
          </RequireAuth>
        ),
        loader: processUrlToken,
        children: [
          {
            path: ACCOUNT_SETUP_PATH,
            children: [
              {
                index: true,
                element: <Navigate to='website' replace />,
              },
              {
                path: ':step',
                lazy: () => import('components/Pages/AccountSetup'),
              },
            ],
          },
          {
            path: '/acc/home-page',
            loader: protectedLoader({
              categories: [PersonCategories.PRODUCER],
            }),
            element: <Homepage />,
            handle: {
              crumb: ({ t }) => ({
                name: t('homepage.title'),
                link: '/acc/home-page',
              }),
            },
          },
          {
            path: '/acc/my-company',
            element: <MyCompany />,
            handle: {
              crumb: ({ t }) => ({
                name: t('common.menu.myCompany'),
              }),
            },
          },
          {
            path: '/acc/company',
            element: <Outlet />,
            handle: {
              crumb: ({ t }) => ({
                name: t('myCompany.title'),
                link: '/acc/my-company',
              }),
            },
            children: [
              {
                path: 'create',
                children: [
                  {
                    index: true,
                    element: <Navigate to='company-info' replace />,
                  },
                  {
                    path: ':step',
                    element: <CreateCompany />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('company.create.addCompany'),
                      }),
                    },
                  },
                ],
              },
              {
                path: ':id',
                lazy: async () => {
                  const { companyNameLoader } = await import(
                    'components/Pages/Company/View'
                  )
                  return {
                    loader: companyNameLoader,
                  }
                },
                handle: {
                  crumb: ({ loaderData, params }) => ({
                    name: loaderData.title,
                    link: `/acc/company/${params.id}`,
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <MyCompany />,
                  },
                  {
                    path: 'edit',
                    element: <EditCompany />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('common.edit'),
                      }),
                    },
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/administration',
            handle: {
              crumb: ({ t }) => ({
                name: t('administration.title'),
                link: '/acc/administration',
                testId: 'breadcrumb-administration-link',
              }),
            },
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            children: [
              {
                index: true,
                element: <AdministrationRedirect />,
              },
              {
                path: 'users',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.users.title'),
                  }),
                },
                loader: protectedLoader({
                  roles: [
                    Roles.SUPERADMIN,
                    Roles.MANAGER,
                    Roles.CONTENT,
                    Roles.ADMIN,
                  ],
                }),
                children: [
                  {
                    index: true,
                    element: <Navigate to='current' />,
                  },
                  {
                    path: ':sub',
                    element: <AdministrationUsers />,
                  },
                ],
              },
              {
                path: 'reviews',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                element: <AdministrationReviews />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.reviews.title'),
                  }),
                },
              },
              {
                path: 'companies',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.companies.title'),
                  }),
                },
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    element: <AdministrationCompanies />,
                  },
                ],
              },
              {
                path: 'offers',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.offers.title'),
                    link: '/acc/administration/offers',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <AdministrationOffers />,
                  },
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                        link: `/acc/administration/offers/${params.id}`,
                      }),
                    },
                    children: [
                      { index: true, element: <Offer /> },
                      {
                        path: 'edit',
                        loader: protectedLoader({
                          roles: [
                            Roles.PERSON,
                            Roles.SUPERADMIN,
                            Roles.MANAGER,
                          ],
                          categories: [
                            PersonCategories.PRODUCER,
                            PersonCategories.WHOLESALER,
                            PersonCategories.RETAILER,
                            PersonCategories.RESTAURANT,
                          ],
                        }),
                        children: [
                          {
                            index: true,
                            element: (
                              <TableContextProvider>
                                <SellWineEditOffer />
                              </TableContextProvider>
                            ),
                          },
                        ],

                        handle: {
                          crumb: ({ t }) => ({
                            name: t('sellWine.myOffers.edit'),
                          }),
                        },
                      },
                    ],
                  },
                  {
                    path: 'add-offer',
                    loader: protectedLoader({
                      roles: [
                        Roles.PERSON,
                        Roles.SUPERADMIN,
                        Roles.MANAGER,
                        Roles.CONTENT,
                      ],
                      categories: [
                        PersonCategories.PRODUCER,
                        PersonCategories.WHOLESALER,
                        PersonCategories.RETAILER,
                        PersonCategories.RESTAURANT,
                      ],
                      // isVerified: true,
                    }),
                    element: (
                      <TableContextProvider>
                        <Outlet />
                      </TableContextProvider>
                    ),
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('Add offer'),
                      }),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to='wine' replace />,
                      },
                      {
                        path: ':tab',
                        element: <SellWineAddNewOfferV2 />,
                      },
                    ],
                  },
                ],
              },

              {
                path: 'requests',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.requests.title'),
                  }),
                },
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <AdministrationRequests />,
                  },
                  {
                    path: 'new-request/:check?',
                    element: <AdministrationNewRequest />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('buyWine.myRequests.addNewRequest.header'),
                      }),
                    },
                  },
                  {
                    path: ':id/matched-producers',
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('administration.matchedProducers.title'),
                      }),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to='all' replace />,
                      },
                      {
                        path: ':tab',
                        element: <AdministrationMatchedProducers />,
                      },
                    ],
                  },
                  {
                    path: ':id/potential-producers',
                    element: <AdministrationPotentialProducers />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('administration.potentialProducers.title'),
                      }),
                    },
                  },
                  {
                    path: ':id',
                    lazy: async () => {
                      const { requestNumberLoader } = await import(
                        'components/Pages/BuyWine/RequestView/_methods'
                      )
                      const { Component } = await import(
                        'components/Pages/BuyWine/RequestView'
                      )
                      return {
                        loader: requestNumberLoader,
                        Component,
                      }
                    },
                    handle: {
                      crumb: ({ loaderData }) => ({
                        name: loaderData.title,
                      }),
                    },
                  },
                ],
              },
              {
                path: 'images',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.images.title'),
                    link: '/acc/administration/images',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <AdministrationImages />,
                  },
                  {
                    path: 'wine/:id',
                    element: <AdministrationWineImages />,
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: t('wineImage.title', { n: params.id }),
                      }),
                    },
                  },
                  {
                    path: 'create',
                    element: <AdministrationImageCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('image.create.title'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'e-labels',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                element: <AdministrationELabels />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.elabels.title'),
                  }),
                },
              },
              {
                path: 'retail-public-pages',
                handle: {
                  crumb: ({ t }) => ({
                    link: ADMINISTRATION_RETAIL_PUBLIC_PAGES,
                    name: t('administration.publicUrls.title'),
                  }),
                },
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    element: <AdministrationPublicPages />,
                  },
                ],
              },
              {
                // TODO: Merge with table route
                path: 'retail-public-page',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    link: ADMINISTRATION_RETAIL_PUBLIC_PAGES,
                    name: t('administration.publicUrls.title'),
                  }),
                },
                children: [
                  {
                    path: ':mode',
                    element: <RetailPublicPageCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('publicPage.create.breadcrumb'),
                      }),
                    },
                  },
                  {
                    path: ':id/:mode',
                    element: <RetailPublicPageEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('publicPage.edit.breadcrumb'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'retail-offers',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.tradeOffers.title'),
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    element: <AdministrationRetailOffers />,
                  },
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                      }),
                    },
                    children: [
                      {
                        path: 'price-history',
                        element: <TradeOfferPriceHistory />,
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('priceHistory.title'),
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
              {
                // TODO: Merge with table
                path: 'retail-offer/:id/:mode',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.tradeOffers.title'),
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <AdministrationRetailOfferEdit />,
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: t(
                          params.mode === 'edit'
                            ? 'tradeOffer.edit.title2'
                            : 'tradeOffer.create.title'
                        ),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'price-sources',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                element: <AdministrationPriceSources />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.priceSources.title'),
                  }),
                },
              },
              {
                path: 'wine-sites',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' />,
                  },
                  {
                    path: ':sub',
                    element: <AdministrationWineSites />,
                  },
                ],
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.wineSites.title'),
                  }),
                },
              },
              {
                // TODO: Merge with table
                path: 'e-shop',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.wineSites.title'),
                    link: '/acc/administration/wine-sites',
                  }),
                },
                children: [
                  {
                    path: 'create',
                    element: <AdministrationEShopCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('merchants.create.title'),
                      }),
                    },
                  },
                  {
                    path: ':id/edit',
                    element: <AdministrationEShopEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('merchants.edit.title'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'mentions',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.mentions.title'),
                    link: '/acc/administration/mentions',
                    testId: 'breadcrumb-mentions-link',
                  }),
                },
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <AdministrationMentions />,
                  },
                  {
                    path: 'create',
                    element: <AdministrationMentionCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('administration.mentions.addNewItem'),
                        testId: 'breadcrumb-current',
                      }),
                    },
                  },
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: t('administration.mentions.edit.title', {
                          n: params.id,
                        }),
                        link: `/acc/administration/mentions/${params.id}/edit`,
                      }),
                    },
                    children: [
                      {
                        path: 'edit',
                        children: [
                          {
                            index: true,
                            element: <Navigate to='source-info' replace />,
                          },
                          {
                            path: ':tab',
                            element: <AdministrationMentionEdit />,
                          },
                        ],
                      },
                      {
                        path: 'cluster',
                        handle: {
                          crumb: ({ t, params }) => ({
                            name: t('administration.mentionCluster.title'),
                            link: `/acc/administration/mentions/${params.id}/cluster`,
                          }),
                        },
                        children: [
                          {
                            index: true,
                            element: <AdministrationMentionsCluster />,
                          },
                          {
                            path: 'link-mentions',
                            element: (
                              <AdministrationMentionsClusterLinkMentions />
                            ),
                            loader: protectedLoader({
                              roles: [Roles.SUPERADMIN, Roles.CONTENT],
                            }),
                            handle: {
                              crumb: ({ t }) => ({
                                name: t('administration.linkMentions.title'),
                              }),
                            },
                          },
                        ],
                      },
                      {
                        path: 'compare',
                        children: [
                          {
                            path: ':similarId',
                            element: <AdministrationMentionCompare />,
                            loader: protectedLoader({
                              roles: [Roles.SUPERADMIN, Roles.CONTENT],
                            }),
                            handle: {
                              crumb: ({ t, params }) => ({
                                name: t('administration.mentions.edit.title', {
                                  n: params.similarId,
                                }),
                                link: `/acc/administration/mentions/${params.similarId}/edit`,
                              }),
                            },
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ':sourceId/path/:destId',
                    element: <AdministrationMentionsClusterPath />,
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: t('administration.mentions.clusterPath.header', {
                          sourceId: params.sourceId,
                          destId: params.destId,
                        }),
                        link: `/acc/administration/mentions/${params.sourceId}/path/${params.destId}`,
                      }),
                    },
                  },
                ],
              },
              {
                path: 'mentions-prices',
                element: <Outlet />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.mentionsPrices.title'),
                    link: '/acc/administration/mentions-prices',
                  }),
                },
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                children: [
                  {
                    index: true,
                    element: <AdministrationMentionsPrices />,
                  },
                  {
                    path: 'verify-retail-offers/:mentionId',
                    element: <AdministrationMentionsPricesVerifyRetailOffers />,
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: `${t(
                          'administration.verifyRetailOffersModal.title'
                        )} / ${params.mentionId}`,
                      }),
                    },
                  },
                ],
              },
              {
                path: 'mentions-facts',
                element: <Outlet />,
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.mentionsFacts.title'),
                    link: '/acc/administration/mentions-facts',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <AdministrationMentionFacts />,
                  },
                  {
                    path: 'verify-facts/:mentionId',
                    element: <AdministrationMentionFactsVerifyFacts />,
                    handle: {
                      crumb: ({ t, params }) => ({
                        name: `${t(
                          'administration.mentionsFacts.verifyFacts.title'
                        )} / ${params.mentionId}`,
                      }),
                    },
                  },
                ],
              },
              {
                path: 'clusters/:tab',
                element: <AdministrationClusters />,
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.clusters.title'),
                  }),
                },
              },
              {
                path: 'verified-relations',
                element: <AdministrationVerifiedRelations />,
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.CONTENT],
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('administration.verifiedRelations.title'),
                  }),
                },
              },
            ],
          },
          {
            path: `/acc/:id`,
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            loader: protectedLoader({
              roles: [Roles.PERSON],
            }),
            handle: {
              crumb: ({ params }) => ({
                name: params.id,
              }),
            },
            children: [
              {
                path: 'price-history',
                element: <TradeOfferPriceHistory />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('priceHistory.title'),
                  }),
                },
              },
            ],
          },
          {
            path: '/acc/retail',
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            loader: protectedLoader({
              roles: [
                Roles.SUPERADMIN,
                Roles.MANAGER,
                Roles.PERSON,
                Roles.CONTENT,
              ],
              requireAccessToRetail: true,
              rejectWhenProhibited: true,
            }),
            handle: {
              crumb: ({ t }) => ({
                name: t('common.menu.retail'),
                link: '/acc/retail',
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='wines/all' replace />,
              },
              {
                path: 'e-shops',
                lazy: () => import('components/Pages/Retail/Eshops'),
              },
              {
                path: 'geo-indications',
                lazy: () => import('components/Pages/Retail/GeoIndications'),
              },
              {
                path: 'wines',
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    lazy: () => import('components/Pages/Retail/Wines'),
                  },
                ],
              },
              {
                path: 'my-range',
                children: [
                  {
                    index: true,
                    lazy: () => import('components/Pages/Retail/MyRange'),
                  },
                ],
              },
              {
                path: 'buyers-wine',
                loader: protectedLoader({
                  roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
                  isVerified: true,
                }),
                children: [
                  {
                    index: true,
                    lazy: () => import('components/Pages/Retail/BuyersWine'),
                  },
                ],
              },
              {
                path: 'price-report',
                lazy: () => import('components/Pages/Retail/PriceReport'),
              },
            ],
          },
          {
            path: '/acc/database',
            loader: protectedLoader({
              roles: [Roles.SUPERADMIN, Roles.MANAGER, Roles.CONTENT],
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('database.title'),
                link: '/acc/database',
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='wines' replace />,
              },
              {
                path: 'producers',
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    lazy: () =>
                      import('components/Pages/DataBase/Content/Producers'),
                  },
                ],
              },
              {
                path: 'brands',
                lazy: () => import('components/Pages/DataBase/Content/Brands'),
              },
              {
                path: 'wines',
                lazy: () => import('components/Pages/DataBase/Content/Wines'),
              },
              {
                path: 'exhibitions',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.exhibitions.title'),
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import('components/Pages/DataBase/Content/Exhibitions'),
                  },
                  {
                    path: 'create',
                    element: <ExhibitionCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('exhibition.create.breadcrumbs.create'),
                      }),
                    },
                  },
                  {
                    path: ':id/edit',
                    element: <ExhibitionEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('exhibition.edit.breadcrumbs'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'awards',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.awards.title'),
                    link: '/acc/database/awards',
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import('components/Pages/DataBase/Content/Awards'),
                  },
                  {
                    path: 'create',
                    element: <AwardCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('award.breadcrumbs.create'),
                      }),
                    },
                  },
                  {
                    path: ':id',
                    lazy: async () => {
                      const { awardNameLoader } = await import(
                        'components/Pages/DataBase/Content/Awards/Rewards'
                      )
                      return {
                        loader: awardNameLoader,
                      }
                    },
                    handle: {
                      crumb: ({ loaderData }) => ({
                        name: loaderData.title,
                        link: `/acc/database/awards/${loaderData.title}`,
                      }),
                    },
                    children: [
                      {
                        index: true,
                        lazy: () =>
                          import(
                            'components/Pages/DataBase/Content/Awards/Rewards'
                          ),
                      },
                      {
                        path: 'edit',
                        element: <AwardEdit />,
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('award.breadcrumbs.edit'),
                          }),
                        },
                      },
                      {
                        path: 'reward/create',
                        element: <RewardCreate />,
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('database.rewards.addNewItem'),
                          }),
                        },
                      },
                      {
                        path: 'reward/:rewardId/edit',
                        element: <RewardEdit />,
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('database.rewards.editReward'),
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: 'geo-indication',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.geoIndication.title'),
                    link: '/acc/database/geo-indication',
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import('components/Pages/DataBase/Content/GeoIndication'),
                  },
                  {
                    path: 'create',
                    element: <GeoIndicationCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('geoIndication.create.breadcrumbs.create'),
                      }),
                    },
                  },
                  {
                    path: ':id/edit',
                    element: <GeoIndicationEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('geoIndication.create.breadcrumbs.create'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'kind-of-wine',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.kindOfWine.title'),
                    link: '/acc/database/kind-of-wine',
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import(
                        'components/Pages/DataBase/Content/KindOfWine/List'
                      ),
                  },
                  {
                    path: 'create',
                    lazy: () =>
                      import(
                        'components/Pages/DataBase/Content/KindOfWine/Create'
                      ),
                  },
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                      }),
                    },
                    children: [
                      {
                        path: 'edit',
                        lazy: () =>
                          import(
                            'components/Pages/DataBase/Content/KindOfWine/Edit'
                          ),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'special-classification-condition',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.specClasCondition.title'),
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import(
                        'components/Pages/DataBase/Content/SpecialClassificationCondition'
                      ),
                  },
                  {
                    path: 'create',
                    lazy: () =>
                      import(
                        'components/Pages/DataBase/Content/SpecialClassificationCondition/Form/Create'
                      ),
                  },
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                      }),
                    },
                    children: [
                      {
                        path: 'edit',
                        lazy: () =>
                          import(
                            'components/Pages/DataBase/Content/SpecialClassificationCondition/Form/Edit'
                          ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/my-wines',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [PersonCategories.PRODUCER],
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('myWines.title'),
                link: '/acc/my-wines/wines',
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='wines/all' replace />,
              },
              {
                path: 'wines',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.wines.title'),
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':sub',
                    lazy: () => import('components/Pages/MyWines/Wines'),
                  },
                ],
              },
              {
                path: 'brands',
                element: <MyWinesBrands />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.brands.title'),
                  }),
                },
              },
              {
                path: 'e-labels/:id/edit/*',
                loader: ({ params }) =>
                  redirect(`/acc/e-labels/${params.id}/edit`),
              },
              {
                path: 'e-labels/*',
                element: <Navigate to='/acc/e-labels' replace />,
              },
            ],
          },
          {
            path: 'e-labels',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [PersonCategories.PRODUCER],
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('myWines.subMenu.eLabels'),
                link: '/acc/e-labels',
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='all' replace />,
              },
              {
                path: ':sub/',
                lazy: () => import('components/Pages/MyWines/ELabels'),
              },
              {
                path: ':id/edit',
                handle: {
                  crumb: ({ params }) => ({
                    name: String(params.id),
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to='general-info' replace />,
                  },
                  {
                    path: ':tab',
                    element: <ELabelEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('elabel.edit.breadcrumb2'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'create',
                element: <ELabelCreate />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('elabel.create.breadcrumb'),
                  }),
                },
              },
            ],
          },
          {
            path: '/acc/captured-wine/:id',
            loader: capturedWineNameLoader,
            handle: {
              crumb: ({ loaderData, params }) => ({
                name: loaderData.title,
                link: `/acc/captured-wine/${params.id}`,
              }),
            },
            children: [
              {
                index: true,
                element: <CapturedWineView />,
              },
              {
                path: 'edit',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('common.edit'),
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <CapturedWineEdit />,
                  },
                  {
                    path: ':tab',
                    element: <CapturedWineEdit />,
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/captured-wines',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [
                PersonCategories.PRODUCER,
                PersonCategories.WHOLESALER,
                PersonCategories.RETAILER,
                PersonCategories.RESTAURANT,
              ],
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('common.menu.capturedWines'),
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='all' replace />,
              },
              {
                path: ':sub',
                element: <CapturedWinesRouter />,
              },
            ],
          },
          {
            path: '/acc/producer',
            children: [
              {
                path: 'create',
                element: <ProducerCreate />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.producers.addNew'),
                  }),
                },
              },
              {
                path: ':id',
                lazy: async () => {
                  const { tradeNameNameLoader } = await import(
                    'components/Pages/Producer/_methods'
                  )
                  return {
                    loader: tradeNameNameLoader,
                  }
                },
                handle: {
                  crumb: ({ loaderData, params }) => ({
                    name: loaderData.title,
                    link: `/acc/producer/${params.id}`,
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <ProducerView />,
                  },
                  {
                    path: 'edit',
                    element: <ProducerEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('common.edit'),
                      }),
                    },
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/brand',
            children: [
              {
                path: 'create',
                element: <CreateBrand />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('myWines.brands.add'),
                  }),
                },
              },
              {
                path: ':id',
                lazy: async () => {
                  const { brandNameLoader } = await import(
                    'components/Pages/Brands'
                  )
                  return {
                    loader: brandNameLoader,
                  }
                },
                handle: {
                  crumb: ({ loaderData, params }) => ({
                    name: loaderData.title,
                    link: `/acc/brand/${params.id}`,
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <ViewBrand />,
                  },
                  {
                    path: 'edit',
                    element: <EditBrand />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('common.edit'),
                      }),
                    },
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/wine',
            children: [
              {
                path: 'create',
                element: <WineCreate />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('database.wines.addNewWine'),
                  }),
                },
              },
              {
                path: ':id',
                loader: wineNameLoader,
                handle: {
                  crumb: ({ loaderData, params }) => ({
                    name: loaderData.title,
                    link: `/acc/wine/${params.id}`,
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <WineRedirectToSlug />,
                  },
                  {
                    path: ':product',
                    element: <WineRedirectToSlug />,
                  },
                  {
                    path: 'edit',
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('common.edit'),
                      }),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to='general' replace />,
                      },
                      {
                        path: ':tab',
                        element: <WineEdit />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/product/:slug',
            loader: productNameLoader,
            handle: {
              crumb: ({ loaderData, params }) => ({
                name: loaderData.title,
                link: `/acc/product/${params.slug}`,
              }),
            },
            children: [
              {
                index: true,
                element: <WineView />,
                loader: async ({ params }) => {
                  const userLoggedIn = isLogin()
                  if (!userLoggedIn) {
                    const lang = getLocaleForPublicReview()
                    const publicProductPage = `/${lang}/product/${params.slug}`
                    return redirectDocument(publicProductPage)
                  }
                  return null
                },
              },
              {
                path: ':geoId',
                element: <WineRating />,
                lazy: async () => {
                  const { geoIndicationNameLoader } = await import(
                    'components/Pages/BuyWine/Analysis/AppellationRating/_methods'
                  )
                  return {
                    loader: geoIndicationNameLoader,
                  }
                },
                handle: {
                  crumb: ({ t, loaderData }) => ({
                    name: `${loaderData.title} ${t(
                      'wine.rating.title.rating'
                    )}`,
                  }),
                },
              },
              {
                path: 'review/:id',
                element: <ReviewView />,
                loader: async ({ params }) => {
                  const userLoggedIn = isLogin()
                  if (!userLoggedIn) {
                    const lang = getLocaleForPublicReview()
                    const publicProductPage = `/${lang}/product/${params.slug}/review/${params.id}`
                    return redirectDocument(publicProductPage)
                  }
                  return null
                },
                handle: {
                  crumb: ({ t, params }) => ({
                    name: t('review.view.reviewTitle', { n: params.id }),
                  }),
                },
              },
            ],
          },
          {
            path: '/acc/product-snapshot/:id',
            element: (
              <Suspense fallback={<PageLoaderWithMenu />}>
                <ProductSnapshotView />
              </Suspense>
            ),
          },
          {
            path: '/acc/user/:id',
            loader: protectedLoader({
              roles: [
                Roles.SUPERADMIN,
                Roles.ADMIN,
                Roles.CONTENT,
                Roles.PERSON,
              ],
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('common.menu.user.title'),
              }),
            },
            children: [
              {
                index: true,
                element: <Navigate to='profile' replace />,
              },
              {
                path: 'profile',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('user.profile.title'),
                  }),
                },
                lazy: async () => {
                  const { Profile } = await import('components/Pages/User')
                  return {
                    Component: Profile,
                  }
                },
              },
              {
                path: 'settings',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('user.settings.title'),
                  }),
                },
                lazy: async () => {
                  const { Settings } = await import('components/Pages/User')
                  return {
                    Component: Settings,
                  }
                },
              },
              {
                path: 'companies',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('user.companies.title'),
                  }),
                },
                lazy: async () => {
                  const { Companies } = await import('components/Pages/User')
                  return {
                    Component: Companies,
                  }
                },
              },
              {
                path: 'notification',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('user.notification.title'),
                  }),
                },
                lazy: async () => {
                  const { Notification } = await import('components/Pages/User')
                  return {
                    Component: Notification,
                  }
                },
              },
            ],
          },
          {
            path: '/acc/sell-wine',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [
                PersonCategories.PRODUCER,
                PersonCategories.WHOLESALER,
                PersonCategories.RETAILER,
                PersonCategories.RESTAURANT,
              ],
              rejectWhenProhibited: true,
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('sellWine.title'),
              }),
            },
            children: [
              {
                path: 'my-offers',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('sellWine.myOffers.title'),
                    link: '/acc/sell-wine/my-offers',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },

                  {
                    path: ':tab',
                    loader: protectedLoader({
                      roles: [Roles.PERSON],
                      categories: [
                        PersonCategories.PRODUCER,
                        PersonCategories.WHOLESALER,
                        PersonCategories.RETAILER,
                        PersonCategories.RESTAURANT,
                      ],
                      requireAccessToSellWine: true,
                    }),
                    lazy: () =>
                      import(
                        'components/Pages/SellWine/MyOffers/MainContainer'
                      ),
                  },
                  {
                    path: 'add-offer',
                    loader: protectedLoader({
                      roles: [
                        Roles.PERSON,
                        Roles.SUPERADMIN,
                        Roles.MANAGER,
                        Roles.CONTENT,
                      ],
                      categories: [
                        PersonCategories.PRODUCER,
                        PersonCategories.WHOLESALER,
                        PersonCategories.RETAILER,
                        PersonCategories.RESTAURANT,
                      ],
                      // isVerified: true,
                    }),
                    element: (
                      <TableContextProvider>
                        <Outlet />
                      </TableContextProvider>
                    ),
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('Add offer'),
                      }),
                    },
                    children: [
                      {
                        index: true,
                        element: <Navigate to='wine' replace />,
                      },
                      {
                        path: ':tab',
                        element: <SellWineAddNewOfferV2 />,
                      },
                    ],
                  },
                ],
              },
              {
                // This should be merged with my-offers path but it uses tab as param
                path: 'offer',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('sellWine.myOffers.title'),
                    link: '/acc/sell-wine/my-offers',
                  }),
                },
                children: [
                  {
                    path: ':id',
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                        link: `/acc/sell-wine/offer/${params.id}`,
                      }),
                    },
                    children: [
                      { index: true, element: <Offer /> },
                      {
                        path: 'edit',
                        loader: protectedLoader({
                          roles: [
                            Roles.PERSON,
                            Roles.SUPERADMIN,
                            Roles.MANAGER,
                          ],
                          categories: [
                            PersonCategories.PRODUCER,
                            PersonCategories.WHOLESALER,
                            PersonCategories.RETAILER,
                            PersonCategories.RESTAURANT,
                          ],
                        }),
                        children: [
                          {
                            index: true,
                            element: (
                              <TableContextProvider>
                                <SellWineEditOffer />
                              </TableContextProvider>
                            ),
                          },
                        ],

                        handle: {
                          crumb: ({ t }) => ({
                            name: t('sellWine.myOffers.edit'),
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: 'my-warehouses',
                loader: protectedLoader({
                  roles: [Roles.PERSON],
                  categories: [
                    PersonCategories.PRODUCER,
                    PersonCategories.WHOLESALER,
                    PersonCategories.RETAILER,
                    PersonCategories.RESTAURANT,
                  ],
                  requireAccessToSellWine: true,
                }),
                element: <SellWineMyWarehouses />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('sellWine.myWarehouses.title'),
                  }),
                },
              },
              {
                path: 'requests',
                loader: protectedLoader({
                  roles: [Roles.PERSON],
                  categories: [
                    PersonCategories.PRODUCER,
                    PersonCategories.WHOLESALER,
                    PersonCategories.RETAILER,
                    PersonCategories.RESTAURANT,
                  ],
                  requireAccessToSellWine: true,
                }),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('sellWine.requests.title'),
                    link: '/acc/sell-wine/requests',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <SellWineRequests />,
                  },
                  {
                    path: ':id',
                    lazy: async () => {
                      const { requestNumberLoader } = await import(
                        'components/Pages/BuyWine/RequestView/_methods'
                      )
                      return {
                        loader: requestNumberLoader,
                      }
                    },
                    handle: {
                      crumb: ({ params, loaderData }) => ({
                        name: loaderData.title,
                        link: `/acc/sell-wine/requests/${params.id}`,
                      }),
                    },
                    children: [
                      {
                        index: true,
                        lazy: () =>
                          import('components/Pages/SellWine/RequestView'),
                      },
                      {
                        path: 'wines',
                        lazy: () =>
                          import('components/Pages/SellWine/RequestWines'),
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('sellWine.requests.winesTitle'),
                          }),
                        },
                      },
                      {
                        path: 'offers',
                        handle: {
                          crumb: ({ t }) => ({
                            name: t('sellWine.requests.offersTitle'),
                          }),
                        },
                        lazy: () =>
                          import('components/Pages/SellWine/RequestOffers'),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/buy-wine',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [
                PersonCategories.WHOLESALER,
                PersonCategories.RETAILER,
                PersonCategories.RESTAURANT,
              ],
              isVerified: true,
              rejectWhenProhibited: true,
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('buyWine.title'),
              }),
            },
            children: [
              {
                path: 'wines',
                element: <BuyWineWines />,
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.wines.title'),
                  }),
                },
              },
              {
                path: 'offers',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.offers.title'),
                    link: '/acc/buy-wine/offers',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <Navigate to='all' replace />,
                  },
                  {
                    path: ':tab',
                    lazy: () => import('components/Pages/BuyWine/Offers'),
                  },
                  {
                    path: 'new-deal',
                    element: <BuyWineAddMyDeal />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('buyWine.offers.addDeal'),
                      }),
                    },
                  },
                ],
              },
              {
                path: 'offer',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.offers.title'),
                    link: '/acc/buy-wine/offers',
                  }),
                },
                children: [
                  {
                    path: ':id',
                    element: <Offer />,
                    handle: {
                      crumb: ({ params }) => ({
                        name: params.id,
                      }),
                    },
                  },
                ],
              },
              {
                path: 'analysis',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.analysis.title'),
                    link: '/acc/buy-wine/analysis',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <BuyWineAnalysis />,
                  },
                  {
                    path: ':slug/:geoId/:offerId',
                    element: <BuyWineAppellationRating />,
                    lazy: async () => {
                      const { geoIndicationNameLoader } = await import(
                        'components/Pages/BuyWine/Analysis/AppellationRating/_methods'
                      )
                      return {
                        loader: geoIndicationNameLoader,
                      }
                    },
                    handle: {
                      crumb: ({ t, loaderData }) => ({
                        name: t('appellationRating.title', {
                          geo: loaderData.title,
                          interpolation: {
                            escapeValue: false,
                          },
                        }),
                      }),
                    },
                  },
                ],
              },
            ],
          },
          {
            path: '/acc/buy-wine/my-requests',
            loader: protectedLoader({
              roles: [Roles.PERSON],
              categories: [
                PersonCategories.WHOLESALER,
                PersonCategories.RETAILER,
                PersonCategories.RESTAURANT,
              ],
              rejectWhenProhibited: true,
            }),
            element: (
              <TableContextProvider>
                <Outlet />
              </TableContextProvider>
            ),
            handle: {
              crumb: ({ t }) => ({
                name: t('buyWine.title'),
              }),
            },
            children: [
              {
                index: true,
                lazy: () => import('components/Pages/BuyWine/MyRequests'),
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.myRequests.title'),
                    link: '/acc/buy-wine/my-requests',
                  }),
                },
              },
              {
                path: 'create/:check?',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.myRequests.title'),
                    link: '/acc/buy-wine/my-requests',
                  }),
                },
                children: [
                  {
                    index: true,
                    element: <BuyWineRequestCreate />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('buyWine.offers.add'),
                      }),
                    },
                  },
                ],
              },
              {
                path: ':id',
                handle: {
                  crumb: ({ t }) => ({
                    name: t('buyWine.myRequests.title'),
                    link: '/acc/buy-wine/my-requests',
                  }),
                },
                children: [
                  {
                    index: true,
                    lazy: async () => {
                      const { requestNumberLoader } = await import(
                        'components/Pages/BuyWine/RequestView/_methods'
                      )
                      const { Component } = await import(
                        'components/Pages/BuyWine/RequestView'
                      )
                      return {
                        loader: requestNumberLoader,
                        Component,
                      }
                    },
                    handle: {
                      crumb: ({ loaderData }) => ({
                        name: loaderData.title,
                      }),
                    },
                  },
                  {
                    path: ':mode/:check?',
                    element: <BuyWineDraftRequestEdit />,
                    handle: {
                      crumb: ({ t }) => ({
                        name: t('buyWine.myRequests.edit'),
                      }),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Error404 />,
  },
])

const App = () => {
  useGTMInit()
  useUserBack()
  useSubscriptions()

  return <RouterProvider router={router} />
}

export default App
